import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'rt.rightPanel.width';
const DEFAULT_WIDTH_PX = 520;
const MIN_WIDTH_PX = 320;
const MAX_WIDTH_FRACTION = 0.6;

interface UseResizablePanelWidthValue {
  readonly width: number;
  readonly startDrag: (event: React.MouseEvent) => void;
  readonly dragging: boolean;
}

/**
 * Width of the right Sider plus the drag handle that lives on its left
 * edge. The user grabs the handle and drags left/right; width is
 * clamped to `[MIN_WIDTH_PX, 60% of viewport]` and persisted in
 * localStorage so it survives reloads.
 *
 * The drag loop listens on `window` (not the handle) so fast flicks
 * that leave the handle don't lose the grip.
 */
export function useResizablePanelWidth(): UseResizablePanelWidthValue {
  const [width, setWidth] = useState<number>(_readWidth);
  const [dragging, setDragging] = useState(false);
  const startXRef = useRef(0);
  const startWRef = useRef(0);

  const onMouseMove = useCallback((event: MouseEvent) => {
    const dx = startXRef.current - event.clientX;
    const raw = startWRef.current + dx;
    setWidth(_clamp(raw));
  }, []);

  const onMouseUp = useCallback(() => {
    setDragging(false);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    // Persist only on release — intermediate values would spam storage.
    setWidth((current) => {
      _writeWidth(current);
      return current;
    });
  }, [onMouseMove]);

  const startDrag = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      startXRef.current = event.clientX;
      startWRef.current = width;
      setDragging(true);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [onMouseMove, onMouseUp, width],
  );

  // Re-clamp on window resize so the panel never exceeds the new max.
  useEffect(() => {
    const onResize = () => setWidth((w) => _clamp(w));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  return { width, startDrag, dragging };
}

function _clamp(raw: number): number {
  const max = Math.floor(window.innerWidth * MAX_WIDTH_FRACTION);
  if (raw < MIN_WIDTH_PX) return MIN_WIDTH_PX;
  if (raw > max) return max;
  return Math.round(raw);
}

function _readWidth(): number {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_WIDTH_PX;
    const n = Number(raw);
    return Number.isFinite(n) ? _clamp(n) : DEFAULT_WIDTH_PX;
  } catch {
    return DEFAULT_WIDTH_PX;
  }
}

function _writeWidth(width: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    // Silent.
  }
}
